// App — entry component for Phase 6 dispatcher-based flow.
//
// URL-driven flow (Phase 6 Task 6 確定方針 S/Y, Task 6.6-B G/H, Task 7 Z/AC/AD):
//   - `?action=create`        → 即 createRoomShortcut → 成功なら pathname を
//                               `/<uuid>` に URL 置換 (本サイト誘導 link 着地点)
//   - `?action=verify&token=` → 即 verifyMagicLink → purpose 別 navigate
//                               (bind_email → `/<id>`, recovery/room_admin → `/admin`)
//                               (Phase 6 Task 7 確定方針 AC/AD)
//   - pathname `/<uuid>`      → ReJoinScreen   (`GET /rooms/:id/server` + display name)
//   - pathname `/create`      → CreateRoomPage (settings form, 「1秒作成 UX」, サブリンク経由)
//   - pathname `/login`       → LoginPage (cross-room ログイン, Phase 6 Task 7 確定方針 Z)
//   - pathname `/admin`       → AdminPage (Phase 6 Task 8)
//   - 何も無し                 → HomePage (1-click 作成 + サブリンク + 参加)
//   - phase = 'joining' / 'joined' / 'rejected' / 'left' は phase-driven 切替
//
// 2026-05 改訂: ルーム URL を `?room=<uuid>` → `/<uuid>` に切替 (= bare slug).
// 2026-05 追加改訂: view 系 URL を `?view=*` → `/login` / `/admin` / `/create`
// に切替. action / token / name 等の query は変更なし (= 識別子・data・
// 一時遷移指示は query のまま). nginx 側は `try_files $uri $uri/ /index.html`
// の SPA fallback がそのまま効く.
//
// HomePage / CreateRoomPage / LoginPage は **history.pushState** + popstate
// 同期で URL 切替を行う. 戻るボタンで前画面に戻れる (Task 6 確定方針 S).
// 共通の URL helper は `./urlNavigation.ts` に集約.
//
// `?action=create` 着地スプリンタ: HomePage 表示を bypass して即 spinner →
// `createRoomShortcut` 呼び出し → 成功なら admin_token を localStorage 保存 +
// URL を `/<uuid>` に **replaceState** (splash 履歴を残さない) → ReJoinScreen.
// 失敗なら HomePage に fallback して inline error を出す.
//
// `?action=verify&token=xxx` 着地スプラッシュ: 同様に splash 表示 →
// `verifyMagicLink` 呼び出し → directive に従って navigate. expired/used/invalid
// 等のエラー directive は ErrorScreen (CTA: ログイン画面に戻る) を出す.
// (Phase 6 Task 7 確定方針 AC/AD)
//
// Phase 6 Task 8 で `AdminPlaceholder` を `AdminPage` に置換 (route は変えない).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createRoomShortcut } from '../application/createRoomShortcut.js';
import { reportLayoutSwitch } from '../application/layoutSwitchAnalytics.js';
import { canRestorePastCanvas } from '../application/pastCanvasList.js';
import { parseRecoverQueryParam } from '../application/recoveryGenerations.js';
import { type VerifyMagicLinkDirective, verifyMagicLink } from '../application/verifyMagicLink.js';
import type { RoomState } from '../domain/RoomState.js';
import { formatHiddenLayerNames } from '../infrastructure/layerVisibilityPreference.js';
import { createLayoutOnboardingSeenStore } from '../infrastructure/layoutOnboardingSeen.js';
import { detectVisitorKind, type VisitorKind } from '../infrastructure/returningVisitor.js';
import { createWhatsNewSeenStore } from '../infrastructure/whatsNewSeen.js';
import { AdminPage } from './AdminPage.js';
import { AnnounceBanner } from './AnnounceBanner.js';
import { AnnounceEntryModal, hasPendingEntryAnnounce } from './AnnounceEntryModal.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { CreateRoomPage } from './CreateRoomPage.js';
import { ErrorScreen } from './ErrorScreen.js';
import { HomePage } from './HomePage.js';
import { useAaSmoothTip } from './hooks/useAaSmoothTip.js';
import { useSession } from './hooks/useSession.js';
import {
  useWhatsNewEntryAutoOpen,
  type WhatsNewEntryGate,
} from './hooks/useWhatsNewEntryAutoOpen.js';
import { KickedScreen } from './KickedScreen.js';
import { LoginPage } from './LoginPage.js';
import { CanvasStage } from './layouts/CanvasStage.js';
import { ClassicLayout } from './layouts/classic/ClassicLayout.js';
import { LayoutToggle, shouldShowOnboardingCoachmark } from './layouts/LayoutToggle.js';
import { ModernLayout } from './layouts/modern/ModernLayout.js';
import { useRoomChrome } from './layouts/shared/useRoomChrome.js';
import { useLayoutPreference } from './layouts/useLayoutPreference.js';
import { useSlotRect } from './layouts/useSlotRect.js';
import { MaintenanceBanner } from './MaintenanceBanner.js';
import { SessionProvider } from './providers/SessionProvider.js';
import { ReJoinScreen } from './ReJoinScreen.js';
import { RestoreUndoToast } from './RestoreUndoToast.js';
import { TroubleTipCard } from './TroubleTipCard.js';
import {
  hardReloadCurrent,
  hardReloadToHome,
  isUuidV7Like,
  navigateToView,
  parseRoomFromPathname,
  parseViewFromPathname,
  replaceUrlToRoom,
  VIEW_PATHS,
} from './urlNavigation.js';
import { WHATS_NEW_ENTRIES } from './whatsNewEntries.js';

interface UrlState {
  /** Room UUID from pathname `/<uuid>` (validated, lowercased). null if absent or malformed. */
  readonly roomId: string | null;
  /** True if pathname is `/create`. */
  readonly viewCreate: boolean;
  /** True if pathname is `/login` (Phase 6 Task 7 確定方針 Z). */
  readonly viewLogin: boolean;
  /** True if pathname is `/admin` (Phase 6 Task 7, 中身は Task 8). */
  readonly viewAdmin: boolean;
  /** True if URL has `?action=create` (本サイト誘導 link 着地点). */
  readonly actionCreate: boolean;
  /**
   * Raw token from `?action=verify&token=xxx`. null if absent.
   * (Phase 6 Task 7 確定方針 AC: magic link 着地点)
   */
  readonly actionVerifyToken: string | null;
  /**
   * Raw single-segment pathname (without leading `/`) when it looks like a
   * room slug but is **not** a valid UUID. Used for the "ルーム ID が不正です"
   * error screen. null if pathname is empty / multi-segment / valid UUID.
   */
  readonly rawRoomSlug: string | null;
}

const readUrlState = (): UrlState => {
  if (typeof window === 'undefined') {
    return {
      roomId: null,
      viewCreate: false,
      viewLogin: false,
      viewAdmin: false,
      actionCreate: false,
      actionVerifyToken: null,
      rawRoomSlug: null,
    };
  }
  const params = new URLSearchParams(window.location.search);
  const action = params.get('action');
  const pathname = window.location.pathname;
  // View 系 page は pathname (`/login` / `/admin` / `/create`) から取る
  // (2026-05 追加改訂、query `?view=` から移行).
  const viewName = parseViewFromPathname(pathname);
  const viewCreate = viewName === 'create';
  const viewLogin = viewName === 'login';
  const viewAdmin = viewName === 'admin';
  const actionCreate = action === 'create';
  // Magic link 着地点は **2 形式** をサポート:
  //   - `?action=verify&token=xxx`            (SPA 内 navigate からの直叩き)
  //   - `/auth/verify?token=xxx`              (dispatcher の `buildMagicLinkUrl`
  //                                            が生成する canonical URL — メール
  //                                            から踏むのはこちら).
  // どちらも token 空文字 / 未指定なら無視 (= null).
  const isVerifyByAction = action === 'verify';
  const isVerifyByPath = pathname === '/auth/verify' || pathname.endsWith('/auth/verify/');
  const tokenRaw = isVerifyByAction || isVerifyByPath ? params.get('token') : null;
  const actionVerifyToken = tokenRaw !== null && tokenRaw.length > 0 ? tokenRaw : null;
  // Room は pathname `/<uuid>` から取る (2026-05 改訂、query `?room=` から移行).
  const roomId = parseRoomFromPathname(pathname);
  // 不正 slug 検出: 単一セグメントだが UUID として valid でない (= 「ルーム ID
  // が不正です」error screen に出す). 予約 path (`/auth/verify` / `/login` /
  // `/admin` / `/create`) や正常 UUID はここでは null になる. magic link 経路や
  // view path は別 branch で先に処理されるので、エラー画面は出ない.
  let rawRoomSlug: string | null = null;
  if (roomId === null && viewName === null) {
    const trimmed = pathname.replace(/^\/+/, '').replace(/\/+$/, '');
    if (trimmed.length > 0 && !trimmed.includes('/') && !isUuidV7Like(trimmed)) {
      rawRoomSlug = trimmed;
    }
  }
  return {
    roomId,
    viewCreate,
    viewLogin,
    viewAdmin,
    actionCreate,
    actionVerifyToken,
    rawRoomSlug,
  };
};

/**
 * React 用 URL state hook. HomePage / CreateRoomPage が pushState +
 * popstate dispatch でナビゲートしたときと、ブラウザ back / forward の両方
 * で再評価する.
 */
const useUrlState = (): UrlState => {
  const [state, setState] = useState<UrlState>(() => readUrlState());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (): void => setState(readUrlState());
    window.addEventListener('popstate', handler);
    return () => {
      window.removeEventListener('popstate', handler);
    };
  }, []);

  return state;
};

/** Map `room.rejected.reason` to a user-facing screen (Phase 4 範囲). */
export const renderRejected = (reason: string, message: string | undefined): React.ReactNode => {
  switch (reason) {
    case 'room-not-found':
      return (
        <ErrorScreen
          title="ルームが見つかりません"
          description="このルーム ID は存在しないか削除されています。新規ルームを作成しますか？"
          cta={{ label: '新規ルームを作成', onActivate: goToCreateScreen }}
          detail={message ?? reason}
        />
      );
    case 'maintenance':
      return (
        <ErrorScreen
          title="メンテナンス中"
          description="サーバはメンテナンス中です。しばらくしてから再度お試しください。"
          detail={message ?? reason}
        />
      );
    case 'rate-limited':
      return (
        <ErrorScreen
          title="リクエストが多すぎます"
          description="しばらくしてから再度お試しください。"
          detail={message ?? reason}
        />
      );
    case 'kicked':
      return (
        <ErrorScreen
          title="入室を拒否されました"
          description="管理者により入室が拒否されました。"
          cta={{ label: '新規ルームを作成', onActivate: goToCreateScreen }}
          detail={message ?? reason}
        />
      );
    case 'banned':
      return (
        // BAN と分かる文言にしない (ban-2026-10-01.md §1). サーバの message / reason は出さない.
        <ErrorScreen
          title="この部屋には入れません"
          description="しばらくしてから、別の部屋をお試しください。"
          cta={{ label: 'トップへ', onActivate: goToCreateScreen }}
        />
      );
    case 'invalid-credential':
      // Phase 6 Task 7: ログイン画面に誘導.
      return (
        <ErrorScreen
          title="認証に失敗しました"
          description="入室情報が正しくありません。ログイン画面から再認証してください。"
          cta={{ label: 'ログイン画面へ', onActivate: goToLoginScreen }}
          detail={message ?? reason}
        />
      );
    default:
      // 2026-07-25 ユーザ指摘: ここには **接続断 (= 自動再接続の打ち切り、
      // SessionProvider が `internal-error` + 「自動再接続に N 回失敗しました。」
      // で dispatch)** も落ちてくる。CTA が「新規ルームを作成」(= `/` へ遷移) だと、
      // 一時的な切断 (Wi-Fi 切替 / スリープ復帰 / サーバ再起動) で **まだ生きている
      // 部屋を捨てさせる** ことになる。復帰したい人が大多数なので現 URL の再読み込みを
      // 既定にする (`hardReloadCurrent` の doc 参照。部屋が消えていた場合は
      // `room-not-found` 画面に落ち、そこに作成導線がある = 作成への道は失われない)。
      return (
        <ErrorScreen
          title="サーバエラーが発生しました"
          description="接続が切れたか、サーバでエラーが発生しました。再読み込みして入り直してください。"
          cta={{ label: '再読み込み', onActivate: hardReloadCurrent }}
          detail={message !== undefined ? `${reason}: ${message}` : reason}
        />
      );
  }
};

/**
 * Force a clean reload back to HomePage (pathname `/` + 全 query クリア).
 * Used by "left" phase and ErrorScreen CTAs — both want a fresh
 * SessionProvider transport so we use `location.assign` rather than
 * pushState + popstate.
 */
const goToCreateScreen = (): void => {
  hardReloadToHome();
};

/**
 * Soft navigate to `/login` (used by ActionVerifySplash CTA after
 * expired/used/invalid magic link). pushState + popstate so the URL change
 * keeps history clean.
 */
const goToLoginScreen = (): void => {
  navigateToView('login');
};

/**
 * Splash component for the `?action=create` landing path. Shown briefly while
 * `createRoomShortcut` is in flight; on success the URL is rewritten to
 * pathname `/<uuid>` (replaceState — keep the address bar clean) which causes
 * RootSwitch to route to ReJoinScreen on the next render. On failure we drop
 * the `action` param and surface the error via the HomePage's inline error.
 *
 * Why a separate component: keeps the RootSwitch switch statement readable
 * and isolates the side-effectful auto-create. Using a hook ensures the call
 * fires exactly once even if RootSwitch re-renders.
 */
const ActionCreateSplash = (): React.ReactNode => {
  const { dispatcher, adminTokens } = useSession();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const out = await createRoomShortcut(dispatcher.createRoom);
      if (cancelled) return;
      if (typeof window === 'undefined') return;
      if (out.outcome === 'ok') {
        adminTokens.save(out.roomId, out.adminToken);
        // Replace the URL (no history entry for the transient splash):
        // pathname `/<uuid>` + 全 query クリア.
        replaceUrlToRoom(out.roomId);
        return;
      }
      // Failure: surface error and drop `action` so HomePage shows up.
      // We can't easily forward the error message into HomePage's local
      // state, so we render it inline here and let the user retry from
      // HomePage.
      setError(out.message);
      const u = new URL(window.location.href);
      u.searchParams.delete('action');
      window.history.replaceState({}, '', u.toString());
      // Don't dispatch popstate — we still want to render the error here
      // until the user navigates. The inline retry button below clears it.
    })();
    return () => {
      cancelled = true;
    };
    // dispatcher / adminTokens are stable singletons from the SessionProvider.
  }, [dispatcher, adminTokens]);

  if (error !== null) {
    return (
      <ErrorScreen
        title="ルーム作成に失敗しました"
        description={error}
        cta={{ label: 'ホームに戻る', onActivate: goToCreateScreen }}
      />
    );
  }

  return (
    <div className="grid h-full place-items-center" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-3">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent"
          aria-hidden="true"
        />
        <p className="text-xs text-[var(--color-text-muted)]">お絵描きルームを準備中…</p>
      </div>
    </div>
  );
};

/**
 * Splash component for the `?action=verify&token=xxx` landing path (Phase 6
 * Task 7 確定方針 AC/AD).
 *
 * Behavior:
 *   1. Show splash while `verifyMagicLink` is in flight.
 *   2. On `navigate-admin` (recovery / room_admin): save adminToken to
 *      localStorage + replaceState pathname `/admin`.
 *   3. On `navigate-room` (bind_email): replaceState pathname `/<roomId>`.
 *   4. On expired/used/invalid/unknown: render ErrorScreen with a CTA back
 *      to LoginPage.
 *
 * Why a separate component (mirror of ActionCreateSplash):
 * - keeps RootSwitch's switch readable.
 * - SPA hydration timing: this MUST run on initial chunk (no lazy load) so
 *   the user lands on the post-verify view as soon as React mounts.
 */
/**
 * In-flight `verifyMagicLink` promises keyed by token. Magic-link tokens are
 * one-shot on the server, so React 19's `<StrictMode>` (which mounts every
 * effect twice in dev) would otherwise burn the token on the first call and
 * see `'used'` on the second. We dedupe at the module level so a re-mount of
 * `ActionVerifySplash` for the same token reuses the already-running promise.
 *
 * The cache entry is **never** evicted within a page lifetime: subsequent
 * navigations to the same token (e.g. browser Back) are also served from
 * cache so we never hit the dispatcher twice for the same token. Memory cost
 * is bounded by the number of distinct tokens the user clicks during the
 * session (single-digit at most).
 */
const verifyInFlight = new Map<string, Promise<VerifyMagicLinkDirective>>();

const ActionVerifySplash = ({ token }: { readonly token: string }): React.ReactNode => {
  const { dispatcher, adminTokens } = useSession();
  const [errorState, setErrorState] = useState<{
    readonly title: string;
    readonly description: string;
    readonly detail?: string;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let pending = verifyInFlight.get(token);
      if (pending === undefined) {
        pending = verifyMagicLink({ dispatcher, token });
        verifyInFlight.set(token, pending);
      }
      const directive = await pending;
      if (cancelled) return;
      if (typeof window === 'undefined') return;
      switch (directive.outcome) {
        case 'navigate-admin': {
          adminTokens.save(directive.roomId, directive.adminToken);
          // dispatcher が `${MAIL_BASE_URL}/auth/verify?token=...` を生成する.
          // pathname を `/admin` に置換、query は全クリア (action / token / name
          // も含む) → AdminPage に着地.
          const u = new URL(window.location.href);
          u.pathname = VIEW_PATHS.admin;
          u.search = '';
          window.history.replaceState({}, '', u.toString());
          window.dispatchEvent(new PopStateEvent('popstate'));
          return;
        }
        case 'navigate-room': {
          // bind_email: 該当 room の pathname `/<id>` に戻す (query 全クリア).
          replaceUrlToRoom(directive.roomId);
          return;
        }
        case 'expired':
          setErrorState({
            title: 'リンクの有効期限切れ',
            description: directive.message,
            detail: 'expired',
          });
          break;
        case 'used':
          setErrorState({
            title: 'リンクは使用済みです',
            description: directive.message,
            detail: 'used',
          });
          break;
        case 'invalid':
          setErrorState({
            title: 'リンクが無効です',
            description: directive.message,
            detail: 'invalid',
          });
          break;
        case 'unknown':
          setErrorState({
            title: 'エラーが発生しました',
            description: directive.message,
          });
          break;
      }
      // Strip `action` + `token` from URL to avoid stale link reuse on reload.
      // path 経由 (`/auth/verify`) で来た場合は path も `/` に戻す.
      const u = new URL(window.location.href);
      u.searchParams.delete('action');
      u.searchParams.delete('token');
      if (u.pathname === '/auth/verify' || u.pathname === '/auth/verify/') {
        u.pathname = '/';
      }
      window.history.replaceState({}, '', u.toString());
    })();
    return () => {
      cancelled = true;
    };
    // dispatcher / adminTokens are stable singletons from SessionProvider.
  }, [dispatcher, adminTokens, token]);

  if (errorState !== null) {
    return (
      <ErrorScreen
        title={errorState.title}
        description={errorState.description}
        cta={{ label: 'ログイン画面に戻る', onActivate: goToLoginScreen }}
        detail={errorState.detail}
      />
    );
  }

  return (
    <div className="grid h-full place-items-center" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-3">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent"
          aria-hidden="true"
        />
        <p className="text-xs text-[var(--color-text-muted)]">マジックリンクを検証中…</p>
      </div>
    </div>
  );
};

// 2026-06-08 Modern Layout shell (docs/_wip/modern-layout.md §9.4 / §9.4.1):
// 入室中の画面 (joined / reconnecting) で Classic ⇄ Modern を per-user に解決する。
//   - 解決チェーン (スライス1 確定スコープ): ユーザ設定 localStorage > グローバル既定
//     Modern。スマホ (小画面) は Modern 固定 + トグル非表示 (= useLayoutPreference)。
//   - 切替トグルは両レイアウト共通 chrome の置き場所 A に出す (§9.4.1 ①)。Classic には
//     `layoutToggle` slot 経由で、Modern は自前 nav に LayoutToggle を埋め込む。
//   - 同一 engine 上で chrome 差し替え (§9.4): SessionProvider が engine を所有するため
//     toggle で RoomState (canvas/members/chat) は保持され、レイアウト component だけが
//     remount される (= per-layout のローカル UI state はリセット、許容スコープ)。
//
// ## §9.7 engine 永続化 (Phase 1 → Phase 2 で完成、2026-06-08)
// レイアウト切替で canvas がクリア/desync するのを根絶するため、engine/canvas/overlay/
// 描画状態 store を切替の **上位 (= この RoomLayout)** に hoist して永続化する:
//   ① 描画状態 store = `useRoomChrome` を RoomLayout で 1 回呼び所有 (chrome)。
//   ② 永続キャンバス舞台 = `CanvasStage` を RoomLayout 直下に absolute 配置し、
//      アクティブ chrome の「空スロット div」矩形 (= useSlotRect) に重ねる。
//   ③ engine 1 回登録 = CanvasStage 内 DrawingCanvas の onEngineReady が registerEngine を
//      呼ぶ。CanvasStage は chrome (Classic/Modern) の再 render では remount しないので
//      engine 登録は 1 回だけ → engineRef が切替で null にならず、取りこぼし窓が消える。
//
// **Phase 2 スコープ (Classic 移行で完成)**: CanvasStage を Classic/Modern 分岐の **外
// (= 直下) に常設** し、両レイアウトで canvas/engine を作り直さない。これで:
//   - 切替時に engine/canvas が remount せず、canvas 内容が保持される (desync 根絶)。
//   - registerEngine が切替を跨いで 1 回のまま = engineRef が null にならず、ライブ
//     他者 stroke の取りこぼし窓が構造的に消える (§9.4「同一 engine 上で chrome 差し替え
//     = 状態保持の即時トグル」の完成)。
// ClassicLayout / ModernLayout はともに **chrome ビュー** (パネル・ツール帯・チャット)
// だけを担い、canvas 領域は **空スロット div** にする (= CanvasStage がその矩形に重なる)。
// Classic の見た目・挙動 (grid 幾何 / 16色+ARGB+マスク / 機能ボタン群 / 筆圧 / pan /
// iPad 対応 / 個人 UI スケール) は不変。
const RoomLayout = ({
  state,
  shownAnnounceIds,
  visitor,
  whatsNewGate,
}: {
  readonly state: RoomState;
  readonly shownAnnounceIds: Set<string>;
  /** アプリ起動時 (入室フォームより前) に判定した、この訪問より前の入室歴。 */
  readonly visitor: VisitorKind;
  /** 「新機能の紹介」の入室時自動表示を 1 入室 1 回に絞る (RootSwitch 所有)。 */
  readonly whatsNewGate: WhatsNewEntryGate;
}): React.ReactNode => {
  // isMobile (= isPhoneViewport: 幅 < 640 ‖ coarse pointer × 短辺 < 640、resize +
  // matchMedia 追従 — 横持ちスマホもフォン扱い) は Modern のシェル分岐 (デスクトップ /
  // フォンシェル、M1-b modern-phone-shell-2026-07-02.md §3.1) に渡す。スマホでは
  // resolveLayout が Modern 固定に倒すので Classic に isMobile は不要。
  const { transport, view } = useSession();
  // §9 layout-switch analytics: 明示トグルの瞬間だけ analytics.layout-switch を
  // 送る。onSwitch は useLayoutPreference の setLayout 内部 (= LAYOUT_KEY 書き込み
  // と同じ呼び出し箇所) からのみ呼ばれ、毎 render / スマホ強制 Modern 解決では
  // 呼ばれない (useLayoutPreference.ts 参照)。
  const { layout, setLayout, isMobile, showToggle } = useLayoutPreference({
    onSwitch: (next) => reportLayoutSwitch(transport, next),
  });

  // ② 計測フローティング・スロット: RoomLayout root を absolute 基準 (parentRef) に、
  //    アクティブ chrome の空スロット div (canvasSlotRef) を計測して CanvasStage を重ねる。
  const roomRootRef = useRef<HTMLDivElement | null>(null);
  const canvasSlotRef = useRef<HTMLDivElement | null>(null);
  const canvasScrollRef = useRef<HTMLDivElement | null>(null);
  const stageRect = useSlotRect(canvasSlotRef, roomRootRef);

  // ① 描画状態 store + engine 接続を RoomLayout が所有 (= chrome ビューより上位)。
  //    canvasScrollRef を渡して imagepaste の viewport-center 算出を Classic 原典と
  //    同一にする (= 共有コントローラで挙動再現)。AA の tri-state 実効値は共通既定 off
  //    (consult 2026-06-13 続報) でレイアウト非依存になったため layout は渡さない。
  const [clearConfirmOpen, setClearConfirmOpen] = useState<boolean>(false);
  const chrome = useRoomChrome(state, setClearConfirmOpen, canvasScrollRef, visitor);
  // 困りごとのコツ (AA): 条件を満たすペンアップ後に小さなカードを出す。
  const aaTip = useAaSmoothTip({
    engine: chrome.engine,
    onAaChange: chrome.onAaChange,
    transport,
  });
  // 入室時モーダルの二重表示回避 (B-2 のレイヤー可視確認を announce の後に出す)。
  // remount (自動再接続の fallback join) 後は既読で出すモーダルが無いので最初から true
  // (= レイヤー可視の確認ダイアログが announce 待ちで永久に出ない事故を避ける).
  const [announceDismissed, setAnnounceDismissed] = useState(
    () =>
      shownAnnounceIds.size > 0 && !hasPendingEntryAnnounce(view.activeAnnounces, shownAnnounceIds),
  );

  // 荒らし復旧 UI「?recover=<seq>」深リンク (vandalism-recovery-ui-2026-09-27.md
  // §2「詳細設計」/ anti-vandalism-2026-09-24.md §5「1」、「入口を『続きを描く』に
  // 統一」)。荒らし検知 Slack 通知のリンクが `?recover=<firstStrokeSeq>` 付きで
  // 部屋 URL を指す。入室後 (= RoomLayout mount) に 1 回だけ読み、オーナーなら
  // 過去キャンバス一覧モーダルを自動で開く (`initialRecoverSeq` prop、対象行の決定
  // — `clearedSeq >= seq` の最小の行、無ければ今の絵 — は `PastCanvasListModal` が
  // 行う)。非オーナーは何もしない (エラーも出さない、§2 確定)。読んだら query を
  // URL から消す (reload で再度開かない、レイアウト切替での remount が無いことは
  // RoomLayout の設計上保証されている)。
  const [recoverDeepLinkSeq, setRecoverDeepLinkSeq] = useState<number | undefined>(undefined);
  // 深リンク入室 (?recover=) は過去キャンバス一覧を自動で開くので、新機能の紹介は今回出さない。
  const recoverLinkEntryRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount 時に一度だけ読む (state.permissions は mount 時点の値で十分 — 権限は welcome で確定済み)。
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const seq = parseRecoverQueryParam(params.get('recover'));
    if (seq === null) return;
    recoverLinkEntryRef.current = true;
    // 非オーナー含め常に query は落とす (誤って共有された link で毎回開かないため)。
    const u = new URL(window.location.href);
    u.searchParams.delete('recover');
    window.history.replaceState({}, '', u.toString());
    if (!canRestorePastCanvas(state.permissions)) return;
    setRecoverDeepLinkSeq(seq);
  }, []);
  // アクティブなレイアウト (Classic/Modern いずれか) が「過去キャンバス一覧を
  // 自動で開いた」ことを通知するコールバック。RoomLayout 側の状態をクリアし、
  // レイアウト切替 (remount) で再度自動オープンしないようにする。
  const handleRecoverDeepLinkConsumed = useCallback((): void => {
    setRecoverDeepLinkSeq(undefined);
  }, []);

  // 入室直後の「新機能の紹介」自動表示 (whats-new-2026-10-15.md「初回表示の条件」)。
  // お知らせの入室モーダル (受付ウィンドウ ENTRY_WINDOW_MS) が先、その後で他の入室時
  // ダイアログ (レイヤー非表示の確認) が閉じるのを待って出す。
  const whatsNewSeenStore = useMemo(() => createWhatsNewSeenStore(), []);
  const whatsNewEntry = useWhatsNewEntryAutoOpen({
    visitor,
    entries: WHATS_NEW_ENTRIES,
    store: whatsNewSeenStore,
    gate: whatsNewGate,
    announceBusy:
      shownAnnounceIds.size > 0 || hasPendingEntryAnnounce(view.activeAnnounces, shownAnnounceIds),
    otherModalOpen: chrome.storedHiddenLayers.length > 0,
    skipThisEntry: recoverLinkEntryRef.current,
  });

  // 切替トグル (置き場所 A、§9.4.1 ①)。Classic は TopBar の slot (layoutToggle) に
  // 流し込み、Modern は自前 nav に埋め込む。スマホ (= showToggle false) では出さない。
  // Classic 着地時は coachmark を出さない (shouldShowOnboardingCoachmark が
  // current!=='modern' で false)。
  const onboardingSeenStore = createLayoutOnboardingSeenStore();
  const toggle = showToggle ? (
    <LayoutToggle
      current={layout}
      onChange={setLayout}
      showOnboardingCoachmark={shouldShowOnboardingCoachmark(layout, onboardingSeenStore.hasSeen())}
      onCoachmarkSeen={() => onboardingSeenStore.markSeen()}
    />
  ) : undefined;

  // chrome ビュー (差し替わる)。Classic/Modern を layout で出し分ける。canvas 本体は
  // どちらも持たず、空スロット div を出すだけ (= CanvasStage が重なる)。
  const chromeView =
    layout === 'modern' ? (
      <ModernLayout
        state={state}
        layout={layout}
        onLayoutChange={setLayout}
        showToggle={showToggle}
        isMobile={isMobile}
        chrome={chrome}
        canvasScrollRef={canvasScrollRef}
        canvasSlotRef={canvasSlotRef}
        clearConfirmOpen={clearConfirmOpen}
        setClearConfirmOpen={setClearConfirmOpen}
        {...(recoverDeepLinkSeq !== undefined ? { initialRecoverSeq: recoverDeepLinkSeq } : {})}
        onRecoverDeepLinkConsumed={handleRecoverDeepLinkConsumed}
        whatsNewAutoOpen={whatsNewEntry.autoOpen}
        onWhatsNewAutoOpened={whatsNewEntry.onAutoOpened}
      />
    ) : (
      <ClassicLayout
        state={state}
        layoutToggle={toggle}
        chrome={chrome}
        canvasScrollRef={canvasScrollRef}
        canvasSlotRef={canvasSlotRef}
        clearConfirmOpen={clearConfirmOpen}
        setClearConfirmOpen={setClearConfirmOpen}
        {...(recoverDeepLinkSeq !== undefined ? { initialRecoverSeq: recoverDeepLinkSeq } : {})}
        onRecoverDeepLinkConsumed={handleRecoverDeepLinkConsumed}
        whatsNewAutoOpen={whatsNewEntry.autoOpen}
        onWhatsNewAutoOpened={whatsNewEntry.onAutoOpened}
      />
    );

  return (
    // RoomLayout root = absolute 基準 (position: relative)。CanvasStage はこの直下・
    // chrome 分岐の **外** に常設し、アクティブ chrome の空スロット矩形に重なる (§9.7)。
    // chrome (Classic/Modern) が切り替わっても CanvasStage は remount しない = engine/
    // canvas が作り直されず内容保持 + registerEngine 1 回のまま (desync 根絶)。
    <div ref={roomRootRef} className="relative h-full">
      {chromeView}
      {/* メンテ予告 / お知らせバナー (Phase 7 Task 7-6 / Phase 9.6). 2026-06-14 修正で
          Classic/Modern の各 chrome から **この RoomLayout 直下へ hoist** (= 入室
          モーダルと同じ流儀)。以前は両 layout が各自所有していたため、Classic⇄Modern
          切替で chrome が remount → dismiss 状態 (useState, memory only) がリセット
          → × で閉じてもバナーが切替で復活していた。chrome 分岐の **外** の RoomLayout
          直下に常設すると RoomLayout 自体は切替で remount しないので、dismiss 状態が
          切替を跨いで保持される。新着 announce id / 新メンテ (startAt 違い) は従来どおり
          再表示。配置は両 banner とも `absolute top-*` で、anchor となる relative box が
          chrome wrapper (relative h-full) → RoomLayout root (relative h-full、同じ box を
          占有) に変わるだけなので、画面上部固定の見た目・stacking 順 (Maintenance 上 /
          Announce 下) は不変。 */}
      <MaintenanceBanner state={view.maintenance} />
      <AnnounceBanner announces={view.activeAnnounces} />
      {/* 入室時お知らせモーダル (2026-06-06 consult). 2026-06-14 修正で
          Classic/Modern の各 chrome から **この RoomLayout 直下へ hoist**。
          以前は両 layout が各自所有していたため、Classic⇄Modern 切替で chrome が
          remount → dismissed (useState, memory only) が false に戻り、毎回再ポップ
          していた (feedback-triage-2026-06-13 §4)。CanvasStage と同じく chrome 分岐の
          **外** に常設すると RoomLayout 自体は切替で remount しないので、dismissed が
          切替を跨いで保持される (= 再表示しない)。受付ウィンドウ (ENTRY_WINDOW_MS) も
          入室=RoomLayout mount を起点に 1 回だけ開くので、layout 非依存になる。
          再入室 (= 新 RoomSession で RoomLayout 再 mount) では従来どおり再表示。
          fixed inset-0 なので chrome の relative wrapper 外に置いても表示位置は不変。 */}
      <AnnounceEntryModal
        activeAnnounces={view.activeAnnounces}
        shownIds={shownAnnounceIds}
        onDismissed={() => setAnnounceDismissed(true)}
      />
      {/* レイヤー可視の復元確認 (2026-08-12、feedback-triage-2026-08-12.md §1 B-2)。
          「消したはずのレイヤー3の線が入り直したら復活して重なった」= 自分で 👁 非表示に
          したことを忘れて再入室し、可視状態が永続化されていないので表示に戻っていた、が
          最有力の読み。

          **黙って復元しない**理由: (a) 根本原因は「隠したことを忘れる」ことなので黙って
          戻すと「なぜ出ないんだ」という同じ混乱が別の形で起きる (b) 共有キャンバスなので
          留守中に他の人がそのレイヤーへ描いている可能性があり、黙って復元すると**他人の
          作業を本人に無断で隠す**ことになる (作者判断 2026-08-12)。

          ConfirmDialog を再利用するのはタッチのゴーストクリック対策ガード (`df1770f` +
          `c0827ec`、oekaki/CLAUDE.md に記録) を継承するため。自前ダイアログを新設すると
          あの罠を踏み直す。

          announce モーダルと同時に出さない (二重モーダル回避) ため、activeAnnounces が
          無いか、announce が閉じられた後にだけ出す。 */}
      <ConfirmDialog
        open={
          chrome.storedHiddenLayers.length > 0 &&
          (view.activeAnnounces.length === 0 || announceDismissed)
        }
        title="非表示のレイヤー"
        message={`前回、${formatHiddenLayerNames(chrome.storedHiddenLayers)}を非表示にしていました。非表示のままにしますか?`}
        confirmLabel="非表示のままにする"
        cancelLabel="すべて表示する"
        onConfirm={chrome.keepStoredLayerVisibility}
        onCancel={chrome.showAllLayers}
      />
      {/* 「元に戻す」トースト (荒らし復旧 UI 第 1 段階、§2「元に戻す」)。レイアウト
          非依存、AnnounceBanner / MaintenanceBanner と同じく RoomLayout 直下に常設。 */}
      <RestoreUndoToast transport={transport} myUid={state.uid} />
      <CanvasStage
        state={state}
        transport={transport}
        chrome={chrome}
        onOwnStrokeEnd={aaTip.onOwnStrokeEnd}
        rect={stageRect}
        scrollRef={canvasScrollRef}
        // custom-cursor-2026-09-19.md §2.5-4: 「作成中バブル」用 transient state.
        cursorDrafts={view.cursorDrafts}
        panActive={chrome.panActive}
        // canvas 枠はレイアウトごとに見た目が違う (Modern = rounded-lg、Classic =
        // outsetFrame bevel + p-2)。各レイアウトの枠を渡して見た目を不変に保つ (§9.7)。
        // Classic はマスク ON でも canvas 枠色を変えない (= 原典どおり border-border 固定)。
        {...(layout === 'classic'
          ? {
              frameBaseClassName: 'border bg-[var(--color-bg-soft)]/40 p-2',
              frameMaskBorderClassName: 'border-[var(--color-border)]',
              frameIdleBorderClassName: 'border-[var(--color-border)]',
              regionVariant: 'classic' as const,
            }
          : {})}
      />
      {aaTip.visible ? (
        <TroubleTipCard onAccept={aaTip.accept} onDismiss={aaTip.dismiss} onDetail={aaTip.detail} />
      ) : null}
    </div>
  );
};

const RootSwitch = ({ visitor }: { readonly visitor: VisitorKind }): React.ReactNode => {
  const { view } = useSession();
  const whatsNewGate = useRef<WhatsNewEntryGate>({ decided: false }).current;
  const url = useUrlState();

  // 「このタブで見せて閉じた」お知らせ id (memory only). 自動再接続の fallback join
  // (resume 失敗) は phase を reconnecting のまま保ち RoomLayout を remount させない
  // (根本対処済み) が、同一 announce id をタブ内で 2 度出さない防御としてここで保持し続ける
  // (RoomLayout が何らかの理由で remount されても再ポップしない).
  // 手動の入室 / 退室 / 拒否 / キック (= idle/left/rejected/kicked を経由) でクリア.
  const shownAnnounceIds = useRef<Set<string>>(new Set()).current;
  useEffect(() => {
    const t = view.phase.t;
    if (t === 'idle' || t === 'left' || t === 'rejected' || t === 'kicked') {
      shownAnnounceIds.clear();
      // 手動の入室 / 退室を経たら、次の入室でまた判断する (出さなかった分は次の入室で出る)。
      whatsNewGate.decided = false;
    }
  }, [view.phase.t, shownAnnounceIds, whatsNewGate]);

  // Memo: 'left' に遷移したら URL を綺麗にして reload (WSClient state を
  // 確実にクリーンにする最も簡単な手段).
  useEffect(() => {
    if (view.phase.t === 'left') {
      goToCreateScreen();
    }
  }, [view.phase.t]);

  // Phase-driven view (joined / joining / reconnecting / rejected / left)
  // が **URL より優先**. ルーム入室中はブラウザ URL がどうあれ ClassicLayout
  // を出す.
  switch (view.phase.t) {
    case 'joined':
    case 'reconnecting':
      // Phase 5 — keep the canvas / member list / chat history visible
      // during the WS dropout window.
      //
      // joined ⇔ reconnecting で **同じ要素型・同じ位置** に RoomLayout を描く。
      // 以前は joined=素の <RoomLayout>、reconnecting=<div> ラップと出力位置の
      // ルート要素型が入れ替わり、React が差分更新できず RoomLayout 配下
      // (CanvasStage / DrawingCanvas) を unmount → 空の canvas + engine を新規
      // mount → 既存の絵が消える不具合があった (§9.7 の CanvasStage hoist と
      // 同じ「phase 遷移で remount させない」意図の漏れ)。外側 div を常設し、
      // 再接続の表示は CanvasStage 内の ReconnectOverlay (帯) が担う (旧バッジは廃止)。
      return (
        <div className="relative h-full">
          <RoomLayout
            state={view.phase.state}
            shownAnnounceIds={shownAnnounceIds}
            visitor={visitor}
            whatsNewGate={whatsNewGate}
          />
        </div>
      );
    case 'rejected':
      return renderRejected(view.phase.reason, view.phase.message);
    case 'kicked':
      // Phase 7 Task 7-4: dedicated kicked-from-room screen with re-entry CTA.
      return (
        <KickedScreen kickerDisplayName={view.phase.kickerDisplayName} roomId={view.phase.roomId} />
      );
    case 'joining':
      return (
        <div className="grid h-full place-items-center">
          <p className="text-xs text-[var(--color-text-muted)]">入室中...</p>
        </div>
      );
    case 'left':
      return (
        <div className="grid h-full place-items-center">
          <p className="text-xs text-[var(--color-text-muted)]">退室しました...</p>
        </div>
      );
    default:
      // case 'idle': URL に従って分岐.

      // 1. URL に room param があり UUID として valid → ReJoin.
      if (url.roomId !== null) {
        return <ReJoinScreen roomId={url.roomId} />;
      }

      // 2. URL の pathname が単一セグメントだが UUID として valid でない →
      //    「ルーム ID が不正です」エラー画面. 予約 path (`/auth/verify`) や
      //    複数セグメントは別 branch で処理されるのでここには来ない.
      if (url.rawRoomSlug !== null) {
        return (
          <ErrorScreen
            title="ルーム ID が不正です"
            description="URL のルーム ID 形式が正しくありません。新規ルームを作成しますか？"
            cta={{ label: '新規ルームを作成', onActivate: goToCreateScreen }}
            detail={url.rawRoomSlug}
          />
        );
      }

      // 3. `?action=create` → 即 createRoomShortcut スプラッシュ
      //    (Phase 6 Task 6.6-B 確定方針 H、本サイト誘導 link 着地点).
      if (url.actionCreate) {
        return <ActionCreateSplash />;
      }

      // 4. `?action=verify&token=xxx` → magic link 検証スプラッシュ
      //    (Phase 6 Task 7 確定方針 AC/AD).
      if (url.actionVerifyToken !== null) {
        return <ActionVerifySplash token={url.actionVerifyToken} />;
      }

      // 5. `/create` → 詳細設定ページ.
      if (url.viewCreate) {
        return <CreateRoomPage />;
      }

      // 6. `/login` → ログイン画面 (Phase 6 Task 7 確定方針 Z).
      if (url.viewLogin) {
        return <LoginPage />;
      }

      // 7. `/admin` → 管理画面 (Phase 6 Task 8).
      if (url.viewAdmin) {
        return <AdminPage />;
      }

      // 8. 何も無し → HomePage (1-click + サブリンク + 参加).
      return <HomePage />;
  }
};

export const App = (): React.ReactNode => {
  // 「この訪問より前に入室したことがあるか」は **アプリ起動時 (入室フォームより前) に一度だけ**
  // 判定して保持する。入室すると displayName / anonUid が同じ訪問の中で書かれるため、
  // 入室後に見ると初めての人も常連扱いになる。
  const visitorRef = useRef<VisitorKind | null>(null);
  if (visitorRef.current === null) visitorRef.current = detectVisitorKind();
  const visitor = visitorRef.current;
  // 初めての人 (と storage 不可の環境を除く) には紹介を出さず、今ある紹介をすべて既読にする。
  useEffect(() => {
    if (visitor === 'first') {
      createWhatsNewSeenStore().markSeen(WHATS_NEW_ENTRIES.map((e) => e.id));
    }
  }, [visitor]);
  return (
    <SessionProvider>
      <RootSwitch visitor={visitor} />
    </SessionProvider>
  );
};
